import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, Edit, Download, Maximize, Minimize } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, API_BASE_URL } from '../../api';
import ReactPlayer from 'react-player';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import Plyr from 'plyr';
import Hls from 'hls.js';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import 'plyr/dist/plyr.css';

// Configuration PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface Module {
  _id: string;
  title: string;
  type: 'video' | 'pdf' | 'text' | 'link' | 'image' | 'document';
  content?: string;
  fileUrl?: string;
  externalUrl?: string;
  duration?: number;
  description?: string;
  thumbnailUrl?: string;
}

const AdminCourseModuleViewer: React.FC = () => {
  // Hooks et état
  const { courseId, sectionId, moduleId } = useParams<{
    courseId: string;
    sectionId: string;
    moduleId: string;
  }>();
  const navigate = useNavigate();
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Références
  const playerRef = useRef<HTMLVideoElement>(null);
  const plyrInstance = useRef<Plyr | null>(null);
  const hlsInstance = useRef<Hls | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch module data
  const fetchModule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/courses/${courseId}/modules/${moduleId}`);
      
      if (!response.data?.data) {
        throw new Error('Module data not found');
      }
      
      setModule(response.data.data);
    } catch (err) {
      console.error('Failed to fetch module:', err);
      setError('Failed to load module');
      toast.error('Failed to load module');
      navigate(`/admin/courses/${courseId}`);
    } finally {
      setLoading(false);
    }
  }, [courseId, moduleId, navigate]);

  useEffect(() => {
    fetchModule();
  }, [fetchModule]);

  // Initialize video player
  useEffect(() => {
    if (!module || module.type !== 'video' || !playerRef.current) return;

    const initializeVideoPlayer = () => {
      const contentUrl = getContentUrl();
      
      // Cleanup previous instances
      if (plyrInstance.current) {
        plyrInstance.current.destroy();
      }
      if (hlsInstance.current) {
        hlsInstance.current.destroy();
      }

      // Handle HLS streams
      if (contentUrl.includes('.m3u8')) {
        if (Hls.isSupported()) {
          hlsInstance.current = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 30
          });
          hlsInstance.current.loadSource(contentUrl);
          hlsInstance.current.attachMedia(playerRef.current!);
          hlsInstance.current.on(Hls.Events.MANIFEST_PARSED, () => {
            playerRef.current?.play().catch(e => console.log('Autoplay prevented:', e));
          });
        } else if (playerRef.current?.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari native HLS support
          playerRef.current!.src = contentUrl;
        }
      } else {
        // Regular video files
        playerRef.current!.src = contentUrl;
      }

      // Initialize Plyr
      plyrInstance.current = new Plyr(playerRef.current!, {
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'captions',
          'settings',
          'pip',
          'airplay',
          'fullscreen'
        ],
        ratio: '16:9',
        keyboard: { focused: true, global: true },
        tooltips: { controls: true, seek: true },
        storage: { enabled: true, key: 'plyr_volume' }
      });

      // Handle fullscreen changes
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };
      
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        if (plyrInstance.current) {
          plyrInstance.current.destroy();
        }
        if (hlsInstance.current) {
          hlsInstance.current.destroy();
        }
      };
    };

    initializeVideoPlayer();
  }, [module]);

  // Helper functions
  const getContentUrl = useCallback(() => {
    if (!module) return '';
    if (module.type === 'link') return module.externalUrl || '';
    if (module.fileUrl) return `${API_BASE_URL}${module.fileUrl}`;
    return module.content || '';
  }, [module]);

  const handleDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Component rendering
  const renderVideoPlayer = useCallback(() => {
    if (!module) return null;
    
    const contentUrl = getContentUrl();
    const isExternalVideo = /youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com/.test(contentUrl);

    return (
      <div 
        ref={containerRef}
        className="relative bg-black rounded-xl overflow-hidden shadow-lg"
      >
        {isExternalVideo ? (
          <ReactPlayer
            src={contentUrl}
            width="100%"
            height="100%"
            controls
            light={module.thumbnailUrl || true}
            config={{
              youtube: {
                playerVars: {
                  modestbranding: 1,
                  rel: 0,
                  showinfo: 0,
                  controls: 1
                }
              } as any,
              vimeo: {
                playerOptions: {
                  byline: false,
                  portrait: false,
                  title: false,
                  color: '1a6542'
                }
              } as any
            }}
            className="react-player"
          />
        ) : (
          <video
            ref={playerRef}
            className="plyr__video w-full h-full"
            playsInline
            controls
            poster={module.thumbnailUrl}
          />
        )}
        
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-gray-800 bg-opacity-70 rounded-full text-white hover:bg-opacity-100 transition-all"
            aria-label="Toggle fullscreen"
          >
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </button>
        </div>
      </div>
    );
  }, [module, getContentUrl, isFullscreen, toggleFullscreen]);

  const renderPdfViewer = useCallback(() => {
    if (!module) return null;
    
    return (
      <div className="flex flex-col items-center bg-white rounded-xl shadow-sm p-4">
        <div className="w-full flex justify-end mb-4">
          <a
            href={getContentUrl()}
            download
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Télécharger PDF
          </a>
        </div>
        
        <div className="w-full overflow-auto" style={{ maxWidth: '100%' }}>
          <Document
            file={getContentUrl()}
            onLoadSuccess={handleDocumentLoadSuccess}
            loading={<div className="text-center py-8">Chargement du PDF...</div>}
            error={<div className="text-brand-500 text-center py-8">Erreur de chargement du PDF</div>}
          >
            <Page 
              pageNumber={pageNumber} 
              width={Math.min(800, window.innerWidth * 0.8)} 
              scale={1.0}
            />
          </Document>
        </div>
        
        {numPages && (
          <div className="mt-4 flex items-center gap-4">
            <button 
              onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
              disabled={pageNumber <= 1}
              className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition-colors"
            >
              Précédent
            </button>
            <span className="text-gray-700">
              Page {pageNumber} sur {numPages}
            </span>
            <button 
              onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
              disabled={pageNumber >= numPages}
              className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition-colors"
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    );
  }, [module, pageNumber, numPages, getContentUrl, handleDocumentLoadSuccess]);

  const renderContent = useCallback(() => {
    if (!module) return null;
    
    switch (module.type) {
      case 'video':
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">{module.title}</h2>
            <div className="aspect-w-16 aspect-h-9">
              {renderVideoPlayer()}
            </div>
          </div>
        );
        
      case 'pdf':
        return renderPdfViewer();
        
      case 'text':
        return (
          <div className="prose max-w-none bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{module.title}</h2>
            <div className="text-gray-700 whitespace-pre-wrap">
              {module.content}
            </div>
          </div>
        );
        
      // ... (autres cas restants)
      
      default:
        return (
          <div className="bg-brand-100 border border-brand-400 text-brand-700 px-4 py-3 rounded-lg">
            Type de module non pris en charge
          </div>
        );
    }
  }, [module, renderVideoPlayer, renderPdfViewer]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-500"></div>
        </div>
      </Layout>
    );
  }

  if (error || !module) {
    return (
      <Layout>
        <div className="text-center py-12 space-y-4">
          <h3 className="text-xl font-medium text-gray-900">{error || 'Module non trouvé'}</h3>
          <button
            onClick={() => navigate(`/admin/courses/${courseId}`)}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Retour au cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate(`/admin/courses/${courseId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour au cours
          </button>
          
          <button
            onClick={() => navigate(`/admin/courses/${courseId}/section/${sectionId}/module/${moduleId}/edit`)}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifier le module
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {renderContent()}
        </div>
        
        {module.description && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Description</h2>
            <p className="text-gray-700">{module.description}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminCourseModuleViewer;